import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Typography,
} from "@mui/material";
import { ForgotPassword } from "../utils/usersAPI";
import { useNavigate } from "react-router-dom";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emailInputRef = useRef(null);
  const navigate = useNavigate();

  const validateEmail = () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError("יש להזין כתובת מייל");
      emailInputRef.current?.focus();
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError("כתובת המייל אינה תקינה");
      emailInputRef.current?.focus();
      return false;
    }

    return true;
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);

    if (error) {
      setError("");
    }

    if (message) {
      setMessage("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError("");
    setMessage("");

    if (!validateEmail()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await ForgotPassword(email.trim());

      setMessage(
        typeof response?.data === "string"
          ? response.data
          : "קישור לאיפוס הסיסמה נשלח לכתובת המייל שהוזנה"
      );
    } catch (err) {
      const serverError = err.response?.data;

      setError(
        typeof serverError === "string"
          ? serverError
          : "אירעה שגיאה בשליחת הקישור. נסי שוב בעוד כמה רגעים"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ffffff",

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#D81B60",
        borderWidth: "2px",
      },

      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d32f2f",
      },

      "&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d32f2f",
        borderWidth: "2px",
      },
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#D81B60",
    },

    "& .MuiInputLabel-root.Mui-error": {
      color: "#d32f2f",
    },

    "& input:focus": {
      outline: "none",
    },
  };

  const buttonFocusStyles = {
    "&:focus-visible": {
      outline: "3px solid #D81B60",
      outlineOffset: "3px",
    },
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: "70vh",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        pt: { xs: 8, md: 17 },
        px: 2,
      }}
    >
      <Container
        maxWidth="xs"
        component="section"
        aria-labelledby="forgot-password-title"
      >
        <Typography
          id="forgot-password-title"
          component="h1"
          variant="h4"
          align="center"
          gutterBottom
        >
          שכחת סיסמה
        </Typography>

        <Typography
          component="p"
          align="center"
          sx={{ mb: 3 }}
        >
          הזיני את כתובת המייל שלך ונשלח אלייך קישור לאיפוס הסיסמה.
        </Typography>

        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit}
          aria-describedby="forgot-password-status"
        >
          <TextField
            inputRef={emailInputRef}
            id="forgot-password-email"
            name="email"
            label="כתובת מייל"
            type="email"
            value={email}
            onChange={handleEmailChange}
            fullWidth
            required
            autoComplete="email"
            autoFocus
            error={Boolean(error)}
            helperText={error || " "}
            disabled={isSubmitting}
            inputProps={{
              "aria-invalid": Boolean(error),
              "aria-describedby": error
                ? "forgot-password-email-helper-text forgot-password-status"
                : "forgot-password-email-helper-text",
            }}
            sx={fieldStyles}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isSubmitting}
            aria-disabled={isSubmitting}
            sx={{
              mt: 1,
              minHeight: 48,
              backgroundColor: "#D81B60",

              "&:hover": {
                backgroundColor: "#ad1457",
              },

              ...buttonFocusStyles,
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress
                  size={22}
                  aria-hidden="true"
                  sx={{
                    ml: 1,
                    color: "inherit",
                  }}
                />

                <span>שולח קישור...</span>
              </>
            ) : (
              "שליחת קישור לאיפוס סיסמה"
            )}
          </Button>

          <Box
            id="forgot-password-status"
            aria-live="polite"
            aria-atomic="true"
            sx={{ mt: 2, minHeight: 28 }}
          >
            {message && (
              <Typography
                role="status"
                sx={{
                  color: "success.dark",
                  textAlign: "center",
                }}
              >
                {message}
              </Typography>
            )}

            {error && (
              <Typography
                role="alert"
                sx={{
                  color: "error.main",
                  textAlign: "center",
                }}
              >
                {error}
              </Typography>
            )}
          </Box>

          <Button
            type="button"
            fullWidth
            onClick={() => navigate("/signIn")}
            disabled={isSubmitting}
            sx={{
              mt: 1,
              minHeight: 44,
              color: "#D81B60",
              ...buttonFocusStyles,
            }}
          >
            חזרה להתחברות
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;